import React, { useCallback, useState } from 'react';
import type { FC } from 'react';
import { css } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { MeetingCard } from './MeetingCard.tsx';
import { MeetingRecordingModal } from './MeetingRecordingModal.tsx';
import { MeetingsEligibilityBanner } from './MeetingsEligibilityBanner.tsx';
import { MeetingsSkeleton } from './MeetingsSkeleton.tsx';
import { useMeetings } from './useMeetings.ts';
import type {
	Meeting,
	MeetingActionItem,
	MeetingDateGroupKey,
	MeetingDateRange,
	MeetingsEligibility,
} from './meetingTypes.ts';

const i18n = defineMessages({
	groupToday: {
		id: 'feed.meetings.group.today',
		defaultMessage: 'Today',
		description: 'Heading for meetings that happened today',
	},
	groupYesterday: {
		id: 'feed.meetings.group.yesterday',
		defaultMessage: 'Yesterday',
		description: 'Heading for meetings that happened yesterday',
	},
	groupThisWeek: {
		id: 'feed.meetings.group.this-week',
		defaultMessage: 'This week',
		description: 'Heading for meetings that happened earlier this week',
	},
	groupEarlier: {
		id: 'feed.meetings.group.earlier',
		defaultMessage: 'Earlier',
		description: 'Heading for meetings older than this week',
	},
	empty: {
		id: 'feed.meetings.empty',
		defaultMessage: 'No meetings in this range. Try widening the date range.',
		description: 'Empty state shown when the user has no meetings in the selected range',
	},
});

const GROUP_HEADINGS: Record<MeetingDateGroupKey, (typeof i18n)[keyof typeof i18n]> = {
	today: i18n.groupToday,
	yesterday: i18n.groupYesterday,
	thisWeek: i18n.groupThisWeek,
	earlier: i18n.groupEarlier,
};

const groupHeadingStyle = css({
	marginTop: token('space.150'),
	marginBottom: token('space.100'),
});

const emptyStyle = css({
	paddingTop: token('space.300'),
	paddingBottom: token('space.300'),
	textAlign: 'center',
});

type MeetingsPanelProps = {
	/** Date range to display (default handled upstream = today). */
	dateRange?: MeetingDateRange;
	/** Story/test override so all eligibility states can be exercised. */
	eligibilityOverride?: MeetingsEligibility;
	/**
	 * Fully override the open-recording behaviour (takes precedence over the
	 * default embed modal). Used by stories/tests to assert click handling
	 * without rendering the smart-card embed.
	 */
	onOpenRecording?: (meeting: Meeting) => void;
	onDeepDive?: (meeting: Meeting) => void;
	onActionClick?: (meeting: Meeting, item: MeetingActionItem) => void;
	onEligibilityCtaClick?: (eligibility: MeetingsEligibility) => void;
};

/**
 * Content rendered under the "Meetings" pill on the For You feed.
 *
 * Handles the full set of shell states: loading skeleton, not-eligible banners
 * (cross-flow / connect-calendar), empty state, and the grouped meeting list.
 * All data comes from the swappable `useMeetings` hook (mock today, Relay later).
 */
export const MeetingsPanel: FC<MeetingsPanelProps> = ({
	dateRange,
	eligibilityOverride,
	onOpenRecording,
	onDeepDive,
	onActionClick,
	onEligibilityCtaClick,
}) => {
	const { eligibility, loading, groups, totalCount } = useMeetings({
		dateRange,
		eligibilityOverride,
	});

	// The recording opened in the embed modal (null = closed).
	const [activeMeeting, setActiveMeeting] = useState<Meeting | null>(null);
	const closeRecording = useCallback(() => setActiveMeeting(null), []);

	const handleOpenRecording = useCallback(
		(meeting: Meeting) => {
			if (onOpenRecording) {
				onOpenRecording(meeting);
				return;
			}
			// Default: play the recording in the smart-card embed modal.
			setActiveMeeting(meeting);
		},
		[onOpenRecording],
	);

	const handleDeepDive = useCallback(
		(meeting: Meeting) => {
			if (onDeepDive) {
				onDeepDive(meeting);
				return;
			}
			if (meeting.notesPageUrl && typeof window.open === 'function') {
				window.open(meeting.notesPageUrl, '_blank', 'noopener,noreferrer');
			}
		},
		[onDeepDive],
	);

	const handleEligibilityCta = useCallback(() => {
		onEligibilityCtaClick?.(eligibility);
	}, [eligibility, onEligibilityCtaClick]);

	if (loading || eligibility === 'loading') {
		return <MeetingsSkeleton />;
	}

	if (eligibility === 'noTwc' || eligibility === 'noConnectedCalendar') {
		return (
			<MeetingsEligibilityBanner eligibility={eligibility} onCtaClick={handleEligibilityCta} />
		);
	}

	if (totalCount === 0) {
		return (
			<div css={emptyStyle} data-testid="meetings-empty">
				<Text color="color.text.subtle">
					<FormattedMessage {...i18n.empty} />
				</Text>
			</div>
		);
	}

	return (
		<div data-testid="meetings-panel">
			<MeetingRecordingModal meeting={activeMeeting} onClose={closeRecording} />
			{groups.map((group) => (
				<section key={group.key} data-testid={`meetings-group-${group.key}`}>
					<div css={groupHeadingStyle}>
						<Text color="color.text.subtle" size="small" weight="medium">
							<FormattedMessage {...GROUP_HEADINGS[group.key]} />
						</Text>
					</div>
					{group.meetings.map((meeting) => (
						<MeetingCard
							key={meeting.id}
							meeting={meeting}
							onOpenRecording={handleOpenRecording}
							onDeepDive={handleDeepDive}
							onActionClick={onActionClick}
						/>
					))}
				</section>
			))}
		</div>
	);
};
